import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { ItOpsControllerService } from 'src/app/admin-api/api/itOpsController.service';
import { IntegrityCategory } from 'src/app/admin-api/model/integrityCategory';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface IntegrityCategoryView extends IntegrityCategory {
  integritysFiltered: any[];
  issuesCount: number;
}

@Component({
  selector: 'app-integrity-dashboard',
  templateUrl: './integrity.component.html',
  styleUrls: ['./integrity.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class IntegrityDashboardComponent implements OnInit {
  integrityData: any[] = [];
  visibleCategories: IntegrityCategoryView[] = [];

  showIntegrityDialog = false;
  showCategoryDialog = false;

  selectedIntegrityId: number | null = null;
  selectedCategoryId: number | null = null;

  categoryFilter: number | 'all' = 'all';
  resolutionFilter = 'all';
  statusFilter = 'all';

  categoryOptions: { label: string; value: number | 'all' }[] = [];
  readonly resolutionOptions = [
    { label: 'Todos os tipos', value: 'all' },
    { label: 'Script SQL', value: 'SQL_SCRIPT' },
    { label: 'Handler', value: 'JAVA_HANDLER' },
    { label: 'Handler Standalone', value: 'JAVA_HANDLER_STANDALONE' },
    { label: 's/automação', value: 'NONE' }
  ];
  readonly statusOptions = [
    { label: 'Todos os status', value: 'all' },
    { label: 'Com problemas', value: 'withIssues' },
    { label: 'Sem problemas', value: 'ok' },
    { label: 'Atualização automática', value: 'auto' }
  ];

  private busy = new Set<number>();
  private busyCategory = new Set<number>();
  private busyCategoryRefresh = new Set<number>();

  expandedCategories: Record<number, boolean> = {};
  tableSelections: Record<number, any[]> = {};

  constructor(
    private message: MessageService,
    private confirm: ConfirmationService,
    private itOpsService: ItOpsControllerService,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title
  ) {}

  ngOnInit(): void {
    this.title.setTitle('Integridades IT Ops');
    this.loadData();
  }

  // ---------- Calculated metrics ----------

  get totalIntegrities(): number {
    return this.visibleCategories.reduce(
      (acc, cat) =>
        acc +
        cat.integritysFiltered.filter((item) => Boolean(item?.enableToViews))
          .length,
      0
    );
  }

  get totalWithIssues(): number {
    return this.visibleCategories.reduce(
      (acc, cat) =>
        acc +
        cat.integritysFiltered.filter(
          (item) =>
            Boolean(item?.enableToViews) &&
            Number(item.integrityCounter ?? 0) > 0
        ).length,
      0
    );
  }

  get totalWithoutIssues(): number {
    return this.visibleCategories.reduce(
      (acc, cat) =>
        acc +
        cat.integritysFiltered.filter(
          (item) =>
            Boolean(item?.enableToViews) &&
            Number(item?.integrityCounter ?? 0) <= 0
        ).length,
      0
    );
  }

  // ---------- Lifecycle & data loading ----------

  async loadData() {
    try {
      LoaderService.showLoader();
      const response = await lastValueFrom(
        this.itOpsService.getIntegrityList()
      );
      this.integrityData = this.normalizeIntegrityList(response);
      this.initializeCategoryState();
      this.buildCategoryOptions();
      this.applyFilters();
    } catch (err) {
      AppDialogService.showErrorDialog(err);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private normalizeIntegrityList(payload: any): any[] {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  }

  private initializeCategoryState(): void {
    this.integrityData.forEach((category) => {
      const id = (category as any).integrityCategoryId as number;
      if (this.expandedCategories[id] === undefined) {
        this.expandedCategories[id] = false;
      }
      if (!this.tableSelections[id]) {
        this.tableSelections[id] = [];
      }
    });
  }

  private buildCategoryOptions(): void {
    const base = [{ label: 'Todas as categorias', value: 'all' as const }];
    const dynamic = this.integrityData.map((cat: any) => ({
      label: cat.integrityCategoryName,
      value: cat.integrityCategoryId
    }));
    this.categoryOptions = [...base, ...dynamic];
  }

  // ---------- Filters ----------

  applyFilters(): void {
    const selectedCategory = this.categoryFilter;

    const categories = this.integrityData.filter((cat: any) => {
      if (selectedCategory === 'all') return true;
      return cat.integrityCategoryId === selectedCategory;
    });

    this.visibleCategories = categories.map((cat: any) => {
      const integritys: any[] = cat.integritys ?? [];
      const filtered = integritys.filter((item) => this.matchesFilters(item));
      const issuesCount = filtered.filter(
        (item) => Number(item.integrityCounter) > 0
      ).length;
      this.ensureCategoryState(cat.integrityCategoryId);
      return {
        ...cat,
        integritysFiltered: filtered,
        issuesCount
      };
    });
  }

  resetFilters(): void {
    this.categoryFilter = 'all';
    this.resolutionFilter = 'all';
    this.statusFilter = 'all';
    this.applyFilters();
  }

  private matchesFilters(item: any): boolean {
    const resolution = String(item?.resolutionType ?? '').toUpperCase();
    const counter = Number(item?.integrityCounter ?? 0);
    const automatic = Boolean(item?.automaticUpdate);

    if (
      this.resolutionFilter !== 'all' &&
      resolution !== this.resolutionFilter
    ) {
      return false;
    }

    if (this.statusFilter === 'withIssues' && counter <= 0) {
      return false;
    }
    if (this.statusFilter === 'ok' && counter > 0) {
      return false;
    }
    if (this.statusFilter === 'auto' && !automatic) {
      return false;
    }

    return true;
  }

  private ensureCategoryState(catId: number): void {
    if (this.expandedCategories[catId] === undefined) {
      this.expandedCategories[catId] = false;
    }
    if (!this.tableSelections[catId]) {
      this.tableSelections[catId] = [];
    }
  }

  // ---------- UI helpers ----------

  isCategoryExpanded(catId: number): boolean {
    return this.expandedCategories[catId] === true;
  }

  toggleCategory(catId: number): void {
    this.expandedCategories[catId] = !this.isCategoryExpanded(catId);
  }

  setExpandedForAll(expanded: boolean, ev?: Event): void {
    ev?.stopPropagation();
    this.integrityData.forEach((cat: any) => {
      const id = (cat as any).integrityCategoryId as number;
      this.expandedCategories[id] = expanded;
    });
    this.visibleCategories.forEach((cat: any) => {
      const id = (cat as any).integrityCategoryId as number;
      this.expandedCategories[id] = expanded;
    });
  }

  hasTableSelection(catId: number): boolean {
    return (this.tableSelections[catId] ?? []).length > 0;
  }

  private getOriginalCategory(catId: number): any {
    return this.integrityData.find(
      (cat: any) => cat.integrityCategoryId === catId
    );
  }

  trackByCategory(index: number, item: any): number {
    return item.integrityCategoryId;
  }

  // ---------- Busy helpers ----------

  isBusy(id: number): boolean {
    return this.busy.has(id);
  }
  private startBusy(id: number) {
    this.busy.add(id);
  }
  private endBusy(id: number) {
    this.busy.delete(id);
  }

  isCategoryBusy(id: number): boolean {
    return this.busyCategory.has(id);
  }
  private startCategoryBusy(id: number) {
    this.busyCategory.add(id);
  }
  private endCategoryBusy(id: number) {
    this.busyCategory.delete(id);
  }

  isCategoryRefreshing(id: number): boolean {
    return this.busyCategoryRefresh.has(id);
  }
  private startCategoryRefresh(id: number) {
    this.busyCategoryRefresh.add(id);
  }
  private endCategoryRefresh(id: number) {
    this.busyCategoryRefresh.delete(id);
  }

  // ---------- Actions ----------

  onTableRowNavigate(row: any, ev?: Event): void {
    if (!row) return;
    if (this.shouldIgnoreRowNavigation(ev)) return;
    const integrityId = Number(row?.integrityId ?? row?.id);
    if (!Number.isFinite(integrityId) || integrityId <= 0) return;
    this.openQueryList(integrityId, ev);
  }

  private shouldIgnoreRowNavigation(ev?: Event): boolean {
    if (!ev) return false;
    const target = ev.target as HTMLElement | null;
    if (!target) return false;
    return !!target.closest(
      [
        'button',
        'a',
        '[role="button"]',
        'input',
        'textarea',
        'select',
        'label',
        '.p-table-checkbox',
        '.p-checkbox',
        '.p-button'
      ].join(', ')
    );
  }

  openQueryList(id: number, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    this.router.navigate(['/it-ops/integrity', id], {
      queryParamsHandling: 'preserve'
    });
  }

  async refresh(id: number, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.isBusy(id)) return;

    this.startBusy(id);
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.itOpsService.refreshIntegrity(id));
      this.message.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Contador atualizado!'
      });
      this.loadData();
    } catch (err) {
      AppDialogService.showErrorDialog(err);
    } finally {
      this.endBusy(id);
      LoaderService.showLoader(false);
    }
  }

  async refreshCategory(cat: IntegrityCategory | any, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    const catId = (cat as any).integrityCategoryId as number;
    if (this.isCategoryRefreshing(catId)) return;

    const original = this.getOriginalCategory(catId);
    const integritys: any[] = original?.integritys ?? [];
    if (!integritys.length) {
      this.message.add({
        severity: 'info',
        summary: 'Nada a atualizar',
        detail: 'Esta categoria não possui integridades.'
      });
      return;
    }

    this.startCategoryRefresh(catId);
    LoaderService.showLoader();

    try {
      const tasks = integritys.map((item) =>
        lastValueFrom(this.itOpsService.refreshIntegrity(item.integrityId))
          .then(() => ({ success: true }))
          .catch(() => ({ success: false }))
      );

      const results = await Promise.all(tasks);

      const successCount = results.filter((r) => r.success).length;
      const errorCount = results.length - successCount;

      this.loadData();

      if (errorCount === 0) {
        this.message.add({
          severity: 'success',
          summary: 'Atualizado',
          detail: `Todas as ${successCount} integridades foram atualizadas.`
        });
      } else {
        this.message.add({
          severity: errorCount === results.length ? 'error' : 'warn',
          summary: 'Execução parcial',
          detail: `${successCount} integridade(s) atualizada(s), ${errorCount} falharam.`
        });
      }
    } finally {
      this.endCategoryRefresh(catId);
      LoaderService.showLoader(false);
    }
  }

  async refreshSelectedIntegrities(cat: IntegrityCategoryView, ev?: Event) {
    ev?.stopPropagation();
    const catId = (cat as any).integrityCategoryId as number;
    const selected = this.tableSelections[catId] ?? [];
    if (!selected.length) {
      this.message.add({
        severity: 'info',
        summary: 'Seleção vazia',
        detail: 'Selecione ao menos uma integridade para atualizar.'
      });
      return;
    }

    this.startCategoryRefresh(catId);
    LoaderService.showLoader();

    try {
      const tasks = selected.map((item) =>
        lastValueFrom(this.itOpsService.refreshIntegrity(item.integrityId))
          .then(() => ({ success: true }))
          .catch(() => ({ success: false }))
      );

      const results = await Promise.all(tasks);

      const successCount = results.filter((r) => r.success).length;
      const errorCount = results.length - successCount;

      this.tableSelections[catId] = [];
      this.loadData();

      if (errorCount === 0) {
        this.message.add({
          severity: 'success',
          summary: 'Atualizado',
          detail: `${successCount} integridade(s) selecionada(s) foram atualizadas.`
        });
      } else {
        this.message.add({
          severity: errorCount === results.length ? 'error' : 'warn',
          summary: 'Execução parcial',
          detail: `${successCount} integridade(s) atualizada(s), ${errorCount} falharam.`
        });
      }
    } finally {
      this.endCategoryRefresh(catId);
      LoaderService.showLoader(false);
    }
  }

  exportCategory(cat: IntegrityCategoryView, ev?: Event): void {
    ev?.stopPropagation();
    const rows = cat.integritysFiltered;
    if (!rows.length) {
      this.message.add({
        severity: 'info',
        summary: 'Nada para exportar',
        detail: 'Não há registros filtrados nesta categoria.'
      });
      return;
    }

    const header = [
      'Nome',
      'Descrição',
      'Tipo',
      'Pendências',
      'Atualizada em',
      'Duração (ms)'
    ];
    const csvRows = rows.map((item) => [
      this.escapeCsv(item.integrityName),
      this.escapeCsv(item.integrityDescription ?? ''),
      this.escapeCsv(this.formatResolutionType(item.resolutionType)),
      item.integrityCounter ?? 0,
      this.formatDate(item.lastRunAt),
      item.durationMs ?? 0
    ]);

    const csvContent = [header, ...csvRows]
      .map((line) => line.join(';'))
      .join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    saveAs(
      blob,
      `integridades-${cat.integrityCategoryName
        .toLowerCase()
        .replace(/\s+/g, '-')}.csv`
    );
  }

  private escapeCsv(value: any): string {
    const str = String(value ?? '');
    if (/[;"\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }

  resolve(id: number, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.isBusy(id)) return;

    const integrityName =
      this.visibleCategories
        .flatMap((cat) => cat.integritysFiltered)
        .find((i) => i.integrityId === id)?.integrityName ?? 'esta integridade';

    this.confirm.confirm({
      header: 'Resolver Problemas',
      message: `Tem certeza que deseja executar a rotina de correção para "${integrityName}"? Esta ação não pode ser desfeita.`,
      icon: 'pi pi-play',
      acceptLabel: 'Resolver',
      rejectLabel: 'Cancelar',
      accept: async () => {
        this.startBusy(id);
        LoaderService.showLoader();
        try {
          const res: any = await lastValueFrom(
            this.itOpsService.resolveIntegrity(id)
          );
          const payload = res?.result ?? res ?? {};
          const success = payload?.success !== false;
          const detailMessage =
            payload?.message ??
            (success
              ? 'Rotina de correção executada com sucesso.'
              : 'Rotina executada com advertências.');
          this.message.add({
            severity: success ? 'success' : 'warn',
            summary: success ? 'Sucesso' : 'Aviso',
            detail: detailMessage
          });
          this.refreshAfterResolve(id);
        } catch (err) {
          AppDialogService.showErrorDialog(err);
          this.endBusy(id);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  private async refreshAfterResolve(id: number) {
    try {
      await lastValueFrom(this.itOpsService.refreshIntegrity(id));
      this.message.add({
        severity: 'info',
        summary: 'Contador atualizado',
        detail: 'Contador atualizado após a resolução.'
      });
      this.loadData();
    } catch (err) {
      this.message.add({
        severity: 'warn',
        summary: 'Contador não atualizado',
        detail:
          'Falha ao atualizar contador automaticamente. Atualize manualmente.'
      });
      this.loadData();
    } finally {
      this.endBusy(id);
    }
  }

  edit(id: number, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    this.selectedIntegrityId = id;
    this.showIntegrityDialog = true;
  }

  delete(id: number, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.isBusy(id)) return;

    this.confirm.confirm({
      header: 'Excluir integridade',
      message: 'Tem certeza que deseja excluir esta integridade?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      accept: async () => {
        this.startBusy(id);
        LoaderService.showLoader();
        try {
          await lastValueFrom(this.itOpsService.deleteIntegrity(id));
          this.message.add({
            severity: 'success',
            summary: 'Excluída',
            detail: 'Integridade removida.'
          });
          this.loadData();
        } catch (err) {
          AppDialogService.showErrorDialog(err);
        } finally {
          this.endBusy(id);
          LoaderService.showLoader(false);
        }
      }
    });
  }

  editCategory(cat: IntegrityCategory, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    this.selectedCategoryId = (cat as any).integrityCategoryId as number;
    this.showCategoryDialog = true;
  }

  deleteCategory(cat: IntegrityCategory, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    const id = (cat as any).integrityCategoryId as number;
    if (this.isCategoryBusy(id)) return;

    this.confirm.confirm({
      header: 'Excluir categoria',
      message: `Tem certeza que deseja excluir a categoria "${cat.integrityCategoryName}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      accept: async () => {
        this.startCategoryBusy(id);
        LoaderService.showLoader();
        try {
          await lastValueFrom(this.itOpsService.deleteCategory(id));
          this.message.add({
            severity: 'success',
            summary: 'Excluída',
            detail: 'Categoria removida.'
          });
          this.loadData();
        } catch (err: any) {
          if (err?.status === 409) {
            this.message.add({
              severity: 'warn',
              summary: 'Não permitido',
              detail:
                'A categoria possui integridades vinculadas. Remova ou mova as integridades antes de excluir.'
            });
          } else if (err?.status === 404) {
            this.message.add({
              severity: 'warn',
              summary: 'Não encontrada',
              detail: 'Categoria não existe mais.'
            });
          } else {
            AppDialogService.showErrorDialog(err);
          }
        } finally {
          this.endCategoryBusy(id);
          LoaderService.showLoader(false);
        }
      }
    });
  }

  onDialogHide(): void {
    this.showIntegrityDialog = false;
    this.showCategoryDialog = false;
    this.selectedIntegrityId = null;
    this.selectedCategoryId = null;
  }

  onIntegritySaved(): void {
    this.showIntegrityDialog = false;
    this.selectedIntegrityId = null;
    this.loadData();
  }

  onCategorySaved(): void {
    this.showCategoryDialog = false;
    this.selectedCategoryId = null;
    this.loadData();
  }

  formatDate(dateLike: any): string {
    if (!dateLike) return '-';
    const d = new Date(dateLike);
    if (isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  }

  formatDuration(msLike: any): string {
    if (msLike == null) return '-';
    const ms = Number(msLike);
    if (!isFinite(ms)) return '-';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    const totalSec = Math.round(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const restMinutes = minutes % 60;
      return `${hours}h ${restMinutes}m`;
    }
    return minutes > 0
      ? `${minutes}m ${seconds.toString().padStart(2, '0')}s`
      : `${seconds}s`;
  }

  formatRelativeTime(dateLike: any): string {
    if (!dateLike) return '-';
    const timestamp = new Date(dateLike).getTime();
    if (isNaN(timestamp)) return '-';
    const diffMs = Date.now() - timestamp;
    if (diffMs < 0) return 'agora';
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'agora';
    if (diffMinutes < 60) return `${diffMinutes} min atrás`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} h atrás`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} dia(s) atrás`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `${diffWeeks} semana(s) atrás`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} mês(es) atrás`;
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} ano(s) atrás`;
  }

  formatResolutionType(type: any): string {
    const normalized = String(type ?? '').toUpperCase();
    switch (normalized) {
      case 'SQL_SCRIPT':
        return 'Script SQL';
      case 'JAVA_HANDLER':
        return 'Handler';
      case 'JAVA_HANDLER_STANDALONE':
        return 'Handler Standalone';
      case 'NONE':
      case '':
      default:
        return 's/automação';
    }
  }

  async drop(event: CdkDragDrop<string[]>): Promise<void> {
    moveItemInArray(
      this.visibleCategories,
      event.previousIndex,
      event.currentIndex
    );

    const visibleIds = this.visibleCategories.map(
      (c: any) => c.integrityCategoryId
    );
    this.integrityData.sort((a: any, b: any) => {
      const idxA = visibleIds.indexOf(a.integrityCategoryId);
      const idxB = visibleIds.indexOf(b.integrityCategoryId);
      if (idxA !== -1 && idxB !== -1) {
        return idxA - idxB;
      }
      return 0;
    });

    const orderedIds = this.visibleCategories.map(
      (c) => (c as any).integrityCategoryId
    );
    try {
      await lastValueFrom(
        this.itOpsService.updateCategoryOrder(orderedIds)
      );
      this.message.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Ordem atualizada!'
      });
    } catch (err) {
      this.message.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Falha ao salvar ordem.'
      });
    }
  }

  async dropIntegrity(
    event: CdkDragDrop<any[]>,
    category: IntegrityCategoryView
  ): Promise<void> {
    moveItemInArray(
      category.integritysFiltered,
      event.previousIndex,
      event.currentIndex
    );

    const catId = (category as any).integrityCategoryId as number;
    const originalCat = this.getOriginalCategory(catId);
    if (!originalCat) {
      this.message.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Categoria não encontrada.'
      });
      return;
    }

    const filteredIds = category.integritysFiltered.map((i) => i.integrityId);
    const originalIntegrities: any[] = originalCat.integritys ?? [];

    const reordered = filteredIds.map((id: number) =>
      originalIntegrities.find((i) => i.integrityId === id)
    );
    const notInFiltered = originalIntegrities.filter(
      (i) => !filteredIds.includes(i.integrityId)
    );

    originalCat.integritys = [...reordered, ...notInFiltered];

    const orderedIds = originalCat.integritys.map((i: any) => i.integrityId);

    try {
      await lastValueFrom(this.itOpsService.updateIntegrityOrder(orderedIds));
      this.message.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Ordem das integridades atualizada!'
      });
    } catch (err) {
      this.message.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Falha ao salvar ordem.'
      });
    }
  }

  trackByIntegrity(index: number, item: any): number {
    return item?.integrityId ?? index;
  }
}
